export const CHECKOUT_LINK_SOURCE = "pdp_cta"

interface CheckoutLinkTarget {
  productId: string
  variantId: string
  isPreview: boolean
}

export const buildCheckoutHref = (pathname: string, search: string, { productId, variantId, isPreview }: CheckoutLinkTarget): string => {
  const params = new URLSearchParams(search)
  params.set("signup", "1")
  params.set("productId", productId)
  params.set("variantId", variantId)
  if (isPreview) params.set("preview", "1")
  else params.delete("preview")
  params.set("source", CHECKOUT_LINK_SOURCE)
  return `${pathname}?${params.toString()}`
}

export const isCheckoutLinkArrival = (search: string): boolean => new URLSearchParams(search).get("source") === CHECKOUT_LINK_SOURCE

export const isCheckoutLinkTap = (search: string, referrer: string, origin: string, pathname: string): boolean => {
  if (!isCheckoutLinkArrival(search)) return false
  if (!referrer) return false
  try {
    const from = new URL(referrer)
    return from.origin === origin && from.pathname === pathname
  } catch {
    return false
  }
}

export const withoutCheckoutLinkSource = (pathname: string, search: string): string => {
  const params = new URLSearchParams(search)
  params.delete("source")
  params.delete(EARLY_TAP_AT_PARAM)
  const query = params.toString()
  return query ? `${pathname}?${query}` : pathname
}

export const CHECKOUT_LINK_ATTRIBUTE = "data-pdp-checkout-link"
export const CHECKOUT_HOLD_ATTRIBUTE = "data-pdp-checkout-pending"
export const CHECKOUT_HOLD_SHEET_ATTRIBUTE = "data-pdp-checkout-hold"
export const CHECKOUT_HOLD_CANCEL_ATTRIBUTE = "data-pdp-checkout-cancel"
export const CHECKOUT_TAP_EVENT = "pdp-checkout-tap"

export const CHECKOUT_TAP_HYDRATION_GRACE_MS = 12_000

export const EARLY_TAP_AT_PARAM = "early_tap"
const EARLY_TAP_MARKER_MAX_AGE_MS = 60_000

const PENDING_TAP_KEY = "__pdpCheckoutTap"
const RELEASE_KEY = "__pdpCheckoutTapRelease"

const SHEET_PARAMS = ["signup", "preview", "source", EARLY_TAP_AT_PARAM]

export type CheckoutHoldReason = "tap" | "open"

export interface HeldCheckoutTap {
  href: string
  tappedAt: number
}

export interface EarlyTapMarker {
  at: number
}

export const holdCheckout = (reason: CheckoutHoldReason): void => document.documentElement.setAttribute(CHECKOUT_HOLD_ATTRIBUTE, reason)

const checkoutHoldReason = (): CheckoutHoldReason | null => {
  const reason = document.documentElement.getAttribute(CHECKOUT_HOLD_ATTRIBUTE)
  return reason === "tap" || reason === "open" ? reason : null
}

export const isCheckoutHoldPending = (): boolean => checkoutHoldReason() !== null

export const dismissCheckoutHold = (onlyReason?: CheckoutHoldReason): void => {
  if (onlyReason && checkoutHoldReason() !== onlyReason) return
  document.documentElement.removeAttribute(CHECKOUT_HOLD_ATTRIBUTE)
}

export const earlyCheckoutTapScript = (graceMs: number, holdOpen: boolean) => `(function(){
var SELECTOR='a[${CHECKOUT_LINK_ATTRIBUTE}]';
var CANCEL='[${CHECKOUT_HOLD_CANCEL_ATTRIBUTE}]';
var HOLD='${CHECKOUT_HOLD_ATTRIBUTE}';
var html=document.documentElement;
var pending=null,node=null,timer=0;
${holdOpen ? "html.setAttribute(HOLD,'open');" : ""}
function linkAt(x,y){
var list=document.querySelectorAll(SELECTOR);
for(var i=0;i<list.length;i++){var r=list[i].getBoundingClientRect();
if(r.width&&r.height&&x>=r.left&&x<=r.right&&y>=r.top&&y<=r.bottom)return list[i];}
return null;
}
function clearNode(){if(node){node.removeAttribute('data-pending');node.removeAttribute('aria-busy');}node=null;}
function drop(){clearTimeout(timer);pending=null;window.${PENDING_TAP_KEY}=undefined;clearNode();}
function stripSheetParams(){var u=new URL(location.href);${JSON.stringify(SHEET_PARAMS)}.forEach(function(k){u.searchParams.delete(k);});history.replaceState(null,'',u.pathname+u.search+u.hash);}
function onClick(e){
if(e.defaultPrevented||e.metaKey||e.ctrlKey||e.shiftKey||e.altKey||e.button!==0)return;
var t=e.target;
if(t&&t.closest&&t.closest(CANCEL)){e.preventDefault();if(html.getAttribute(HOLD)==='open')stripSheetParams();drop();html.removeAttribute(HOLD);return;}
var a=t&&t.closest?t.closest(SELECTOR):null;
if(!a&&(t===html||t===document.body))a=linkAt(e.clientX,e.clientY);
if(!a)return;
var href=a.getAttribute('href');
if(!href)return;
e.preventDefault();
drop();
var at=Date.now();
pending={href:href,tappedAt:at};
node=a;
a.setAttribute('data-pending','');
a.setAttribute('aria-busy','true');
html.setAttribute(HOLD,'tap');
window.${PENDING_TAP_KEY}=pending;
window.dispatchEvent(new Event('${CHECKOUT_TAP_EVENT}'));
timer=setTimeout(function(){if(window.${PENDING_TAP_KEY}===pending)location.href=href+(href.indexOf('?')<0?'?':'&')+'${EARLY_TAP_AT_PARAM}='+at;},${graceMs});
}
document.addEventListener('click',onClick,true);
window.${RELEASE_KEY}=function(){document.removeEventListener('click',onClick,true);clearNode();var p=pending;pending=null;window.${PENDING_TAP_KEY}=undefined;return p;};
})()`

export const releaseEarlyCheckoutTap = (): HeldCheckoutTap | null => {
  const release = (window as unknown as Record<string, unknown>)[RELEASE_KEY]
  return typeof release === "function" ? ((release as () => HeldCheckoutTap | null)() ?? null) : null
}

export const readEarlyTapMarker = (search: string, now = Date.now()): EarlyTapMarker | null => {
  const at = Number(new URLSearchParams(search).get(EARLY_TAP_AT_PARAM))
  if (!Number.isInteger(at) || at <= 0 || now - at > EARLY_TAP_MARKER_MAX_AGE_MS) return null
  return { at }
}
